import {
  Button,
  Card,
  CardBody,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Slider,
} from '@nextui-org/react';
import React, { useEffect, useMemo, useState } from 'react';

import { PartnerInfo } from '../types';

interface CreateOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    qty: number,
    discount_percentage: number,
    discount_name: string
  ) => void;
  isSingleTrialUnit?: boolean;
  partnerInfo: PartnerInfo | undefined;
}

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSingleTrialUnit = false,
  partnerInfo,
}) => {
  const [quantity, setQuantity] = useState<number>(isSingleTrialUnit ? 1 : 10);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const regularPrice = 259;

  useEffect(() => {
    setQuantity(isSingleTrialUnit ? 1 : 10);
  }, [isSingleTrialUnit]);

  const discountPercentage = useMemo(() => {
    if (isSingleTrialUnit) return 30.0;
    if (quantity === 10) return 35.0;
    if (quantity === 20) return 38.0;
    if (quantity === 30) return 42.0;
    if (quantity === 40) return 44.0;
    return 0;
  }, [quantity, isSingleTrialUnit]);

  const discountedPrice = useMemo(() => {
    return regularPrice * (1 - discountPercentage / 100);
  }, [discountPercentage]);

  const totalCost = useMemo(() => {
    return discountedPrice * quantity;
  }, [discountedPrice, quantity]);

  const totalSavings = useMemo(() => {
    return regularPrice * quantity - totalCost;
  }, [quantity, totalCost]);

  const discountName = useMemo(() => {
    return `${partnerInfo?.company_name || 'Authorized Partner'} - ${discountPercentage}%`;
  }, [partnerInfo?.company_name, discountPercentage]);

  const handleSubmit = () => {
    onSubmit(quantity, discountPercentage, discountName);
    setIsLoading(true);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 pb-2">
          <h3 className="text-lg font-bold text-slate-800">Place New Order</h3>
          <p className="text-xs text-slate-400 font-normal">
            Configure your order details below
          </p>
        </ModalHeader>
        <ModalBody className="gap-5">
          <Input
            type="text"
            label="Product"
            defaultValue="AIRLOCK One"
            disabled
            variant="bordered"
            radius="sm"
            classNames={{ label: 'text-slate-500', input: 'text-slate-800' }}
          />
          {!isSingleTrialUnit && (
            <Slider
              label="Order Quantity"
              color="secondary"
              size="md"
              hideValue
              showSteps
              step={10}
              minValue={10}
              maxValue={40}
              marks={[
                { value: 10, label: '10' },
                { value: 20, label: '20' },
                { value: 30, label: '30' },
                { value: 40, label: '40' },
              ]}
              defaultValue={10}
              value={quantity}
              onChange={value => setQuantity(value as number)}
              className="max-w-md"
            />
          )}
          <Card className="bg-slate-50 border border-slate-200 shadow-none">
            <CardBody className="p-5 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Partner Discount</span>
                <span className="text-sm font-bold text-airlock-purple">
                  {discountPercentage}% OFF
                </span>
              </div>
              <Divider />
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Unit Price</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400 line-through">
                    ${regularPrice.toFixed(2)}
                  </span>
                  <span className="text-sm font-semibold text-slate-800">
                    ${discountedPrice.toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Quantity</span>
                <span className="text-sm font-semibold text-slate-800">
                  {quantity} units
                </span>
              </div>
              <Divider className="bg-slate-300" />
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-slate-700">Order Total</span>
                <span className="text-lg font-bold text-slate-800">
                  ${totalCost.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-end">
                <span className="text-xs font-medium text-emerald-600">
                  You save ${totalSavings.toFixed(2)}
                </span>
              </div>
            </CardBody>
          </Card>
        </ModalBody>
        <ModalFooter className="pt-2">
          <Button variant="bordered" onPress={onClose} radius="sm" className="border-slate-300 text-slate-600">
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isLoading}
            className="bg-airlock-purple font-medium"
            radius="sm"
          >
            Proceed to Checkout
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateOrderModal;
